import { useState } from "react"
import { IconSquaresConnectedOutline18 as SquaresConnected } from "nucleo-ui-outline-18"

import { providerUrlError } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { Button, Input } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { VendorPreset } from "./presets"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { generateId } from "@/lib/id"
import { useZero } from "@/lib/zero"
import { SettingsContainer } from "../../settings-switches/settings-container"
import { saveConnector } from "./connector-form"
import { presetAuthHeader, presetServerUrl } from "./presets"

interface Values {
	url: string
	key: string
	user: string
	secret: string
}

// Field-level messages, so the admin sees which box is missing before a test.
export function vendorFormErrors(
	preset: VendorPreset,
	values: Values,
): Partial<Record<keyof Values, string>> {
	const errors: Partial<Record<keyof Values, string>> = {}
	if (preset.url.kind === "template" && !values.url.trim()) {
		errors.url = `Enter the ${preset.url.label.toLowerCase()}`
	} else {
		const urlError = providerUrlError(presetServerUrl(preset, values.url))
		if (urlError) errors.url = urlError
	}
	if (preset.auth.kind === "bearer" && !values.key.trim()) {
		errors.key = `Enter the ${preset.auth.label}`
	}
	if (preset.auth.kind === "basic-bearer") {
		if (!values.user.trim()) errors.user = "Enter the username"
		if (!values.secret.trim()) errors.secret = "Enter the secret"
	}
	return errors
}

export function VendorSetupForm({
	preset,
	nextOrder,
	onSaved,
}: {
	preset: VendorPreset
	nextOrder: number
	onSaved: (id: string) => void
}) {
	const zero = useZero()
	const listTools = api.customData.listMcpTools.useMutation()
	const [values, setValues] = useState<Values>({
		url: preset.url.kind === "editable" ? preset.url.url : "",
		key: "",
		user: "",
		secret: "",
	})
	const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>(
		{},
	)
	const [testResult, setTestResult] = useState<{
		success: boolean
		message: string
	} | null>(null)
	const [saving, setSaving] = useState(false)

	const update = (field: keyof Values) => (value: string) => {
		setValues((current) => ({ ...current, [field]: value }))
		setErrors((current) => ({ ...current, [field]: undefined }))
		setTestResult(null)
	}

	const validate = () => {
		const next = vendorFormErrors(preset, values)
		setErrors(next)
		return Object.keys(next).length === 0
	}

	const header = () => presetAuthHeader(preset, values)

	const runTest = async () => {
		if (!validate()) return
		analytics.track("sidebar-section-test", { preset: preset.id })
		const auth = header()
		try {
			const result = await listTools.mutateAsync({
				url: presetServerUrl(preset, values.url),
				headers: auth ? [auth] : [],
			})
			if (!result.success) {
				setTestResult({ success: false, message: result.error })
				return
			}
			const count = result.tools.length
			setTestResult({
				success: true,
				message: `Connected to ${preset.title}. ${count} ${count === 1 ? "action" : "actions"} found.`,
			})
		} catch {
			setTestResult({
				success: false,
				message: `Could not reach ${preset.title}. Try again.`,
			})
		}
	}

	const connect = async () => {
		if (!validate()) return
		setSaving(true)
		const auth = header()
		const id = generateId()
		const created = await saveConnector(
			zero.mutate(
				mutators.sidebarSections.create({
					id,
					title: preset.title,
					url: presetServerUrl(preset, values.url),
					description: preset.summary,
					type: "MCP",
					order: nextOrder,
					availableToAgent: false,
					allowedMcpTools: [],
					headers: auth ? [{ id: generateId(), ...auth }] : [],
				}),
			),
		)
		setSaving(false)
		if (!created) return
		if (preset.publicKnowledge) {
			zero.mutate(
				mutators.sidebarSections.setPublicKnowledge({
					id,
					reachableWithoutIdentity: true,
				}),
			)
		}
		analytics.track("sidebar-section-create", { preset: preset.id })
		onSaved(id)
	}

	const helpLink =
		preset.auth.kind === "none" ? null : (
			<a
				href={preset.auth.helpUrl}
				target="_blank"
				rel="noreferrer"
				className="underline decoration-gray-300 underline-offset-2 hover:text-primary dark:decoration-gray-600"
			>
				Where to find it
			</a>
		)

	return (
		<SettingsContainer className="space-y-4">
			{preset.url.kind === "template" ? (
				<Input
					name="url"
					label={preset.url.label}
					placeholder={preset.url.placeholder}
					hint={preset.url.hint}
					value={values.url}
					errorMessage={errors.url}
					onChange={(event) => update("url")(event.target.value)}
				/>
			) : null}
			{preset.url.kind === "editable" ? (
				<Input
					name="url"
					label="Server URL"
					hint={preset.url.hint}
					value={values.url}
					errorMessage={errors.url}
					onChange={(event) => update("url")(event.target.value)}
				/>
			) : null}

			{preset.auth.kind === "bearer" ? (
				<Input
					name="key"
					type="password"
					autoComplete="off"
					label={preset.auth.label}
					hint={
						<>
							{preset.auth.hint} {helpLink}
						</>
					}
					value={values.key}
					errorMessage={errors.key}
					onChange={(event) => update("key")(event.target.value)}
				/>
			) : null}
			{preset.auth.kind === "basic-bearer" ? (
				<>
					<Input
						name="user"
						autoComplete="off"
						label={preset.auth.userLabel}
						value={values.user}
						errorMessage={errors.user}
						onChange={(event) => update("user")(event.target.value)}
					/>
					<Input
						name="secret"
						type="password"
						autoComplete="off"
						label={preset.auth.secretLabel}
						hint={
							<>
								{preset.auth.hint} {helpLink}
							</>
						}
						value={values.secret}
						errorMessage={errors.secret}
						onChange={(event) => update("secret")(event.target.value)}
					/>
				</>
			) : null}

			{testResult ? (
				<div
					className={cn(
						"text-xs",
						testResult.success
							? "text-green-600 dark:text-green-400"
							: "text-red-600 dark:text-red-400",
					)}
				>
					{testResult.message}
				</div>
			) : null}

			<div className="flex items-center justify-end gap-2">
				<Button
					variant="outline"
					onClick={() => void runTest()}
					isLoading={listTools.isLoading}
				>
					Test connection
				</Button>
				<Button
					Icon={SquaresConnected}
					isLoading={saving}
					onClick={() => void connect()}
				>
					Connect {preset.title}
				</Button>
			</div>
		</SettingsContainer>
	)
}
